import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { RRule } from 'rrule';
import { DAYS_OF_WEEK } from '@features/appointments';

// Frequency options for repeat dropdown
const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'day', rruleValue: RRule.DAILY },
  { value: 'weekly', label: 'week', rruleValue: RRule.WEEKLY },
  { value: 'monthly', label: 'month', rruleValue: RRule.MONTHLY },
  { value: 'yearly', label: 'year', rruleValue: RRule.YEARLY },
];

export interface CustomRecurrenceRule {
  frequency: string;
  interval: number;
  weekDays: string[];
  endMode: 'never' | 'on' | 'after';
  endDate: string | null;
  endCount: number | null;
  // Optional additional fields for more advanced patterns
  monthlyPattern?: 'dayOfMonth' | 'dayOfWeek';
  monthlyDay?: number; // For the day of month pattern (1-31)
  monthlyDayPosition?: 1 | 2 | 3 | 4 | -1; // 1st, 2nd, 3rd, 4th, or last (of month)
}

interface CustomRecurrenceModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (recurrenceRule: CustomRecurrenceRule) => void;
  initialRule?: Partial<CustomRecurrenceRule>;
}

const CustomRecurrenceModal: React.FC<CustomRecurrenceModalProps> = ({
  open,
  onClose,
  onSave,
  initialRule,
}) => {
  // State for recurrence settings
  const [frequency, setFrequency] = useState<string>(initialRule?.frequency || 'weekly');
  const [interval, setInterval] = useState<number>(initialRule?.interval || 1);
  const [weekDays, setWeekDays] = useState<string[]>(initialRule?.weekDays || ['MO']);
  const [endMode, setEndMode] = useState<'never' | 'on' | 'after'>(initialRule?.endMode || 'never');
  const [endDate, setEndDate] = useState<string | null>(initialRule?.endDate || null);
  const [endCount, setEndCount] = useState<number | null>(initialRule?.endCount || null);

  // Additional state for more advanced patterns (optional implementation)
  const [monthlyPattern, setMonthlyPattern] = useState<'dayOfMonth' | 'dayOfWeek'>(
    initialRule?.monthlyPattern || 'dayOfMonth',
  );
  const [monthlyDay, setMonthlyDay] = useState<number>(
    initialRule?.monthlyDay || new Date().getDate(),
  );
  const [monthlyDayPosition, setMonthlyDayPosition] = useState<1 | 2 | 3 | 4 | -1>(
    initialRule?.monthlyDayPosition || 1,
  );

  // Reset state if initialRule changes
  useEffect(() => {
    if (initialRule) {
      setFrequency(initialRule.frequency || 'weekly');
      setInterval(initialRule.interval || 1);
      setWeekDays(initialRule.weekDays || ['MO']);
      setEndMode(initialRule.endMode || 'never');
      setEndDate(initialRule.endDate || null);
      setEndCount(initialRule.endCount || null);

      // Optional advanced properties
      if (initialRule.monthlyPattern) {
        setMonthlyPattern(initialRule.monthlyPattern);
      }
      if (initialRule.monthlyDay) {
        setMonthlyDay(initialRule.monthlyDay);
      }
      if (initialRule.monthlyDayPosition) {
        setMonthlyDayPosition(initialRule.monthlyDayPosition);
      }
    }
  }, [initialRule, open]);

  // Set default day of the week to the current day when frequency changes to weekly
  useEffect(() => {
    if (frequency === 'weekly' && weekDays.length === 0) {
      const today = new Date();
      const dayIndex = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const defaultDay = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][dayIndex];
      setWeekDays([defaultDay]);
    }
  }, [frequency, weekDays]);

  // Handle selecting days of the week
  const handleDayToggle = (_event: React.MouseEvent<HTMLElement>, newDays: string[]) => {
    // Don't allow empty selection
    if (newDays.length > 0) {
      setWeekDays(newDays);
    }
  };

  // Helper to generate a human-readable preview of the recurrence pattern
  const getRecurrencePreview = (): string => {
    try {
      let preview = `Repeats every ${interval > 1 ? interval : ''} `;

      switch (frequency) {
        case 'daily':
          preview += `${interval > 1 ? 'days' : 'day'}`;
          break;

        case 'weekly':
          preview += `${interval > 1 ? 'weeks' : 'week'}`;
          if (weekDays.length > 0) {
            const dayNames = weekDays.map(day => {
              const dayObj = DAYS_OF_WEEK.find(d => d.value === day);
              return dayObj ? dayObj.fullName : day;
            });

            if (dayNames.length === 1) {
              preview += ` on ${dayNames[0]}`;
            } else if (dayNames.length === 7) {
              preview += ' on every day';
            } else {
              const lastDay = dayNames.pop();
              preview += ` on ${dayNames.join(', ')}${
                dayNames.length > 0 ? ' and ' : ''
              }${lastDay}`;
            }
          }
          break;

        case 'monthly':
          preview += `${interval > 1 ? 'months' : 'month'}`;
          break;

        case 'yearly':
          preview += `${interval > 1 ? 'years' : 'year'}`;
          break;
      }

      // Add end condition
      if (endMode === 'on' && endDate) {
        preview += ` until ${new Date(endDate).toLocaleDateString()}`;
      } else if (endMode === 'after' && endCount) {
        preview += `, ${endCount} ${endCount === 1 ? 'time' : 'times'}`;
      }

      return preview;
    } catch (error) {
      return 'Custom recurrence';
    }
  };

  // Handle saving the custom recurrence
  const handleSave = () => {
    const recurrenceRule: CustomRecurrenceRule = {
      frequency,
      interval,
      weekDays,
      endMode,
      endDate,
      endCount,
      // Additional optional properties
      monthlyPattern,
      monthlyDay,
      monthlyDayPosition,
    };

    onSave(recurrenceRule);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
     maxWidth={"sm"} slotProps={{ paper: {
        sx: {
          borderRadius: 2,
        },
      } }}>
      <DialogTitle>Custom recurrence</DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Preview of recurrence pattern */}
          <Box
            sx={{
              mb: 3,
              px: 1,
              py: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {getRecurrencePreview()}
            </Typography>
          </Box>

          {/* Repeat frequency */}
          <Stack direction="row" spacing={2} sx={{alignItems: "center",  mb: 3 }}>
            <Typography>Repeat every</Typography>
            <TextField
              type="number"
              size="small"
              value={interval}
              onChange={e => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: '70px' }}
             slotProps={{ input: {
                inputProps: { min: 1, max: 99 },
              } }}/>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={frequency} onChange={e => setFrequency(e.target.value)}>
                {FREQUENCY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label + (interval > 1 ? 's' : '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Day selection - only show for weekly frequency */}
          {frequency === 'weekly' && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ mb: 1 }}>Repeat on</Typography>
              <ToggleButtonGroup
                value={weekDays}
                onChange={handleDayToggle}
                aria-label="days of week"
                size="small"
                color="primary"
                sx={{
                  '& .MuiToggleButtonGroup-grouped': {
                    borderRadius: '50%!important',
                    mx: 0.5,
                    border: 1,
                    width: 36,
                    height: 36,
                    textTransform: 'none',
                  },
                }}
              >
                {DAYS_OF_WEEK.map(day => (
                  <ToggleButton key={day.value} value={day.value} aria-label={day.fullName}>
                    {day.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Monthly pattern options - only show for monthly frequency (optional feature) */}
          {frequency === 'monthly' && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ mb: 1 }}>Repeat by</Typography>
              <RadioGroup
                value={monthlyPattern}
                onChange={e => setMonthlyPattern(e.target.value as 'dayOfMonth' | 'dayOfWeek')}
                row
              >
                <FormControlLabel
                  value="dayOfMonth"
                  control={<Radio />}
                  label={`Day ${monthlyDay}`}
                />
                <FormControlLabel value="dayOfWeek" control={<Radio />} label="Day of week" />
              </RadioGroup>

              {monthlyPattern === 'dayOfMonth' && (
                <TextField
                  type="number"
                  label="Day of month"
                  size="small"
                  value={monthlyDay}
                  onChange={e =>
                    setMonthlyDay(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))
                  }
                  sx={{ mt: 1, width: '100px' }}
                 slotProps={{ input: {
                    inputProps: { min: 1, max: 31 },
                  } }}/>
              )}

              {monthlyPattern === 'dayOfWeek' && (
                <Stack direction="row" spacing={2} sx={{alignItems: "center",  mt: 1 }}>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <Select
                      value={monthlyDayPosition}
                      onChange={e => setMonthlyDayPosition(e.target.value as 1 | 2 | 3 | 4 | -1)}
                    >
                      <MenuItem value={1}>First</MenuItem>
                      <MenuItem value={2}>Second</MenuItem>
                      <MenuItem value={3}>Third</MenuItem>
                      <MenuItem value={4}>Fourth</MenuItem>
                      <MenuItem value={-1}>Last</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ width: 120 }}>
                    <Select
                      value={weekDays[0] || 'MO'}
                      onChange={e => setWeekDays([e.target.value])}
                    >
                      {DAYS_OF_WEEK.map(day => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Box>
          )}

          {/* End options */}
          <Box>
            <Typography sx={{ mb: 1 }}>Ends</Typography>
            <RadioGroup
              value={endMode}
              onChange={e => setEndMode(e.target.value as 'never' | 'on' | 'after')}
            >
              <FormControlLabel value="never" control={<Radio />} label="Never" sx={{ mb: 1 }} />
              <FormControlLabel
                value="on"
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography>On</Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={endDate || ''}
                      onChange={e => setEndDate(e.target.value)}
                      disabled={endMode !== 'on'}
                     slotProps={{ inputLabel: { shrink: true } }}/>
                  </Stack>
                }
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                value="after"
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography>After</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={endCount || ''}
                      onChange={e =>
                        setEndCount(e.target.value ? Math.max(1, parseInt(e.target.value)) : null)
                      }
                      disabled={endMode !== 'after'}
                      sx={{ width: '70px' }}
                     slotProps={{ input: {
                        inputProps: { min: 1, max: 99 },
                      } }}/>
                    <Typography>occurrences</Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomRecurrenceModal;
